import { useState } from 'react';
import { tl, metaOf, scoreColor, starsStr } from '../utils/helpers';

export default function Card({ item, featured, liked, watchlisted, onLike, onWatchlist, onTrailer, delay = 0 }) {
  const [hov, setHov] = useState(false);

  const typeBadge = {
    fontFamily: 'var(--font-mono)', fontSize: '0.6rem', letterSpacing: '0.1em',
    textTransform: 'uppercase', padding: '0.25rem 0.65rem', borderRadius: 6,
    backdropFilter: 'blur(12px)', fontWeight: 500,
    ...(item.type === 'movie'
      ? { background: 'rgba(255,77,122,.15)',  border: '1px solid rgba(255,77,122,.4)',  color: '#ff4d7a' }
      : item.type === 'book'
      ? { background: 'rgba(56,189,248,.13)',  border: '1px solid rgba(56,189,248,.35)', color: '#38bdf8' }
      : { background: 'rgba(34,197,94,.12)',   border: '1px solid rgba(34,197,94,.32)',  color: '#22c55e' }),
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--surface)', border: `1px solid ${hov ? 'var(--border3)' : 'var(--border)'}`,
        borderRadius: 20, overflow: 'hidden', cursor: 'pointer',
        transition: 'all 0.32s cubic-bezier(0.2,0,0,1)',
        transform: hov ? 'translateY(-5px) scale(1.005)' : 'translateY(0) scale(1)',
        boxShadow: hov ? '0 28px 72px rgba(0,0,0,.55)' : 'none',
        animation: `cardIn 0.5s ease both`,
        animationDelay: `${delay}ms`,
        gridColumn: featured ? 'span 2' : 'span 1',
      }}
    >
      {/* Image */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <img
          src={item.img} alt={item.title} loading="lazy"
          style={{ width: '100%', height: featured ? 320 : 210, objectFit: 'cover', display: 'block', transition: 'transform 0.55s ease', transform: hov ? 'scale(1.07)' : 'scale(1)' }}
        />
        {/* Gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, var(--surface) 100%)' }} />

        {/* Badges */}
        <div style={{ position: 'absolute', top: '0.8rem', left: '0.8rem', right: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={typeBadge}>{tl(item.type)}</span>
          <span style={{ background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '0.3rem 0.65rem', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: scoreColor(item.score) }}>★ {item.score}%</span>
        </div>

        {/* Trailer play button (movies only) */}
        {item.type === 'movie' && item.trailerUrl && (
          <button
            onClick={e => { e.stopPropagation(); onTrailer(item); }}
            style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              width: 54, height: 54, borderRadius: '50%',
              background: 'rgba(255,77,122,.9)', border: '2.5px solid rgba(255,255,255,.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.25rem', cursor: 'pointer', transition: 'all 0.25s',
              opacity: hov ? 1 : 0, boxShadow: '0 4px 24px rgba(255,77,122,.5)',
            }}
            title="Watch Trailer"
          >▶</button>
        )}

        {/* Hover quick-actions */}
        <div style={{ position: 'absolute', bottom: '0.8rem', right: '0.8rem', display: 'flex', gap: '0.4rem', opacity: hov ? 1 : 0, transform: hov ? 'translateY(0)' : 'translateY(6px)', transition: 'all 0.25s' }}>
          {[
            { icon: liked ? '♥' : '♡', active: liked, color: 'var(--rose)', onClick: e => { e.stopPropagation(); onLike(item.id); } },
            { icon: watchlisted ? '✓' : '+', active: watchlisted, color: 'var(--green)', onClick: e => { e.stopPropagation(); onWatchlist(item); } },
          ].map(({ icon, active, color, onClick }, i) => (
            <button key={i} onClick={onClick} style={{
              width: 34, height: 34, borderRadius: 8,
              background: active ? color : 'rgba(0,0,0,.78)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${active ? color : 'rgba(255,255,255,.12)'}`,
              color: active ? '#040508' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.22s',
            }}>{icon}</button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '1.15rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: featured ? '1.45rem' : '1.15rem', fontWeight: 700, lineHeight: 1.2, flex: 1 }}>{item.title}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gold)', whiteSpace: 'nowrap', letterSpacing: '-0.05em' }}>{starsStr(item.stars)}</div>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.64rem', color: 'var(--muted)', marginBottom: '0.7rem' }}>{metaOf(item)} · {item.stars}/5 · {item.runtime}</div>
        <p style={{ fontSize: '0.83rem', color: 'var(--text2)', lineHeight: 1.65, marginBottom: '0.85rem' }}>{item.desc}</p>

        {/* Reason box */}
        <div style={{ background: 'linear-gradient(135deg,rgba(0,212,180,.05),rgba(124,107,255,.03))', border: '1px solid rgba(0,212,180,.14)', borderRadius: 9, padding: '0.65rem 0.85rem', marginBottom: '0.85rem' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.58rem', color: 'var(--teal)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '0.22rem' }}>Why for you</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.55, fontStyle: 'italic' }}>{item.reason}</div>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.85rem' }}>
          {item.tags.map(t => (
            <span key={t} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: '0.61rem', fontFamily: 'var(--font-mono)', padding: '0.18rem 0.5rem', borderRadius: 5 }}>{t}</span>
          ))}
        </div>

        {/* Footer buttons */}
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', paddingTop: '0.8rem', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => onLike(item.id)} style={{ background: liked ? 'rgba(255,77,122,.08)' : 'transparent', border: `1px solid ${liked ? 'var(--rose)' : 'var(--border)'}`, color: liked ? 'var(--rose)' : 'var(--muted)', padding: '0.4rem 0.75rem', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.22s', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>{liked ? '♥ Liked' : '♡ Like'}</button>
          <button onClick={() => onWatchlist(item)} style={{ background: watchlisted ? 'rgba(34,197,94,.07)' : 'transparent', border: `1px solid ${watchlisted ? 'var(--green)' : 'var(--border)'}`, color: watchlisted ? 'var(--green)' : 'var(--muted)', padding: '0.4rem 0.75rem', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.22s', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>{watchlisted ? '✓ Saved' : '+ Watchlist'}</button>
          {item.type === 'movie' && item.trailerUrl && (
            <button onClick={() => onTrailer(item)} style={{ background: 'linear-gradient(135deg,var(--rose),#c4004d)', color: '#fff', border: 'none', padding: '0.4rem 1rem', borderRadius: 8, fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>▶ Trailer</button>
          )}
          <button style={{ background: 'linear-gradient(135deg,var(--teal),#00a89a)', color: '#040508', border: 'none', padding: '0.4rem 1rem', borderRadius: 8, fontSize: '0.76rem', fontWeight: 800, marginLeft: 'auto' }}>Details →</button>
        </div>
      </div>
    </div>
  );
}
